import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { combineLatest, Observable, of } from 'rxjs';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';

import { CoreObject } from '@summit/core';
import { SummitIdObject } from '@summit/models';
import { CrudCacheParameters } from '@summit/services';
import { BlurDirective } from '@summit/ui';

import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputText } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SummitControl } from '../../models';
import { SFormErrorInputComponent } from '../s-form-error-input';

@Component({
  selector: 's-form-input-select',
  templateUrl: './s-form-input-select.component.html',
  styleUrls: ['./s-form-input-select.component.scss'],
  standalone: true,
  imports: [
    InputText,
    InputGroupModule,
    FloatLabelModule,
    SelectModule,
    SFormErrorInputComponent,
    AsyncPipe,
    ReactiveFormsModule,
    FormsModule,
    BlurDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputSelectComponent<T extends SummitIdObject> extends SummitControl<T> implements OnInit {
  @Input() defaultText: string;
  @Input() filter: string[] | number[];
  @Input() filter$: Observable<(string | number)[]> = of([]);
  @Input() nonFormValue: unknown;
  // if deselect is true, then entry will not automatically select the first entry
  @Input() deselect = false;
  @Input() items$: Observable<any[]>;
  @Input() cacheParameters: Observable<CrudCacheParameters>;
  @Input() allowZero = false;
  @Input() enum = false;
  @Input() displayProperty: string;
  @Output() triggerChange: EventEmitter<any> = new EventEmitter<any>();

  private currentValue: any[];

  //@ViewChild('control') dropdownComponent;
  // this.dropdownComponent.focusViewChild.nativeElement.focus();
  // this.dropdownComponent.applyFocus()

  id: string;
  public displayItems$: Observable<any[]>;

  constructor() {
    super();
  }

  get itemsCache() {
    return this.currentValue;
  }

  override ngOnInit() {
    super.ngOnInit();
    //this.displayItems$ = this.items?.asObservable();

    //console.log(this.id, typeof this.id);
    this.id = this.getID();
    this.displayProperty ??= this.enum ? 'value' : 'name';

    if (this.schema) {
      this.attribute = this.schema.getAttribute(this.fieldName as keyof T & string, this.groupName);
      if (!this.attribute) {
        console.error(
          `Could not find Property ${this.fieldName?.toString()} on branch ${this.groupName} on tree:`,
          this.schema
        );
      } else {
        // console.log(`Selecting between ${this.label} and ${this.property.displayName}`);
        this.label = this.label ? this.label : (this.attribute.displayName ?? '');
        this.defaultText = this.defaultText ? this.defaultText : `Select an ${this.label}`;
      }
    } else {
      if (this.schema != false) console.error(`No Schema provided`);
    }
    if (this.items$) {
      //console.log("filter$", this.filter$)
      this.displayItems$ = combineLatest([
        this.items$?.pipe(startWith([])) ?? of([]),
        this.filter$?.pipe(startWith([])) ?? of([])
        //this.myControl?.valueChanges.pipe(startWith(this.myControl?.value)) ?? of([])
      ]).pipe(
        tap(([entries, _filterIds]) => {
          this.currentValue = entries;
          //console.log('value', value);
        }),
        map(([entries, filterIds]) =>
          entries.filter(
            (entry) =>
              entry &&
              (!filterIds || filterIds.findIndex((filter) => filter == entry.id) == -1) &&
              (!this.filter || this.filter.findIndex((filter) => filter == entry.id) == -1)
          )
        ),
        tap((x) => {
          if (x.length > 0) {
            const currentItem = x.find((entry) => entry.id == this.myControl?.value);
            if (this.fieldName == 'incentiveId')
              console.log(`${this.fieldName.toString()} currentItem`, currentItem, this.myControl?.value);
            if (currentItem && !(currentItem instanceof CoreObject)) {
              // if (this.fieldName == 'companyId')
              //   console.log(
              //     `Setting ${this.fieldName.toString()} to ${this.myControl?.value}`,
              //     this.myControl?.value,
              //     x
              //   );
              this.myControl?.patchValue(this.myControl?.value);
            }
          }
        })
      );
      this.displayItems$.pipe(takeUntil(this.disposing$)).subscribe((x) => {
        //if (this.fieldName == null) console.log('displayed Items:', x);
        if (x?.length === 1 && this.isRequired() && !(x instanceof CoreObject)) {
          //console.log(`Single Entry: Setting ${this.fieldName?.toString()} to ${x[0].id}`, x);
          this.form?.controls[this.fieldName ?? '']?.patchValue(x[0].id);
          this.cd.markForCheck();
        }
      });
    } else {
      console.error(`No Items were provided`);
    }
    if (this.disabled) {
      this.form?.controls[this.fieldName ?? '']?.disable();
    }
    //console.log(`${this.fieldName}: this.form`, this.form.controls[this.fieldName ?? '']);
  }

  getID(): string {
    let response = '';
    if (this.groupName) {
      response += this.groupName + '_';
    }
    response += this.fieldName?.toString();
    return response;
  }

  getValue(): any {
    return this.nonFormValue ?? this.form?.controls[this.fieldName ?? '']?.value;
  }
  getItem(): string {
    const tempValue = this.getValue();
    const item = this.currentValue?.find((entry) => entry.id === tempValue);
    return item?.toShortString ? item.toShortString() : item?.value;
  }

  getObject(): any {
    const tempValue = this.getValue();
    return this.currentValue?.find((entry) => entry.id === tempValue);
  }

  onChange(): void {
    //console.log(`Selecting ${this.getValue()} for ${this.fieldName}`);
    this.triggerChange.emit(this.getValue());
  }

  setValue(id: string | number) {
    //this.control.writeValue(id);
    //console.log(`Setting ${this.fieldName} to ${id}`);
    this.form?.controls[this.fieldName ?? '']?.setValue(id);
    this.cd.detectChanges();
  }

  clear(): void {
    //console.log(`Setting value to defaultText: ${this.defaultText}`)
    this.setValue(this.defaultText);
    //console.log(`current form values: `, this.form.value)
  }
}
